<template>
  <v-container fluid>
    <ContentHeader
      header-icon="mdi-receipt-text-outline"
      header-title="Invoices"
      back-navigation
      back-navigation-href="/admin"
      back-navigation-text="To Old Admin"
    >
    </ContentHeader>
    <div v-if="!isEmpty(annualSpend)">
      <v-list-item two-line>
        <v-list-item-content>
          <v-list-item-title class="headline">
            {{ annualSpend.annualSpend | formatDollars }}
          </v-list-item-title>
          <v-list-item-subtitle>
            Annual spend for vendor number {{ annualSpend.vendorNumber }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
      <v-list-item three-line v-for="cov in annualSpend.covs" :key="cov.id">
        <v-list-item-content>
          <v-list-item-title class="headline">
            {{ cov.annualSpend | formatDollars }}
          </v-list-item-title>
          <v-list-item-subtitle>
            Annual spend for COV ID: {{ cov.id }}
          </v-list-item-subtitle>
          <v-list-item-subtitle>
            Vendor group annual spend:
            {{ cov.groupAnnualSpend | formatDollars }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </div>
    <PtSheet>
      <v-form @submit.prevent>
        <div class="d-flex flex-wrap filter-bar mb-4">
          <v-text-field
            class="mr-2"
            label="Vendor Number"
            v-model="searchFilters.vendorNumber"
            outlined
            dense
            hide-details="auto"
          />
          <v-text-field
            class="mr-2"
            label="COV ID"
            v-model="searchFilters.covId"
            outlined
            dense
            hide-details="auto"
          />
          <v-text-field
            class="mr-2"
            label="COVG ID"
            v-model="searchFilters.covgId"
            outlined
            dense
            hide-details="auto"
          />
          <v-text-field
            class="mr-2"
            label="Signup Code"
            v-model="searchFilters.signupCode"
            outlined
            dense
            hide-details="auto"
          />
          <v-select
            class="mr-2"
            label="Link Options"
            outlined
            dense
            :items="linkOptions"
            item-text="text"
            item-value="value"
            v-model="searchFilters.linkOption"
            hide-details="auto"
          />
          <v-select
            class="mr-4"
            label="Include in Annual Spend"
            outlined
            dense
            :items="includeInAnnualSpendOptions"
            item-text="text"
            item-value="value"
            v-model="searchFilters.includeInAnnualSpendOption"
            hide-details="auto"
          />
          <v-btn
            class="mr-2"
            outlined
            type="submit"
            color="primary"
            @click="search()"
            :block="$vuetify.breakpoint.xsOnly"
          >
            Search
          </v-btn>
          <v-btn
            outlined
            color="primary"
            @click="reset()"
            :block="$vuetify.breakpoint.xsOnly"
          >
            Reset
          </v-btn>
          <v-spacer />
        </div>
      </v-form>
      <v-data-table
        dense
        :headers="headers"
        :items="items"
        :server-items-length="itemsLength"
        :loading="dataTableLoading"
        loading-text="Loading... Please wait"
        :options.sync="searchOptions"
        :footer-props="{ itemsPerPageOptions: [50, 100, 250, 1000] }"
      >
        <template v-slot:item.vendorNumber="{ item }">
          <v-btn
            text
            color="colorNeutralsNavy400"
            class="text-decoration-underline"
            @click="setVendorNumber(item.vendorNumber)"
          >
            {{ item.vendorNumber }}
          </v-btn>
        </template>
        <template v-slot:item.covId="{ item }">
          <v-btn
            text
            color="colorNeutralsNavy400"
            class="text-decoration-underline"
            @click="setCovId(item.covId)"
          >
            {{ item.covId }}
          </v-btn>
        </template>
        <template v-slot:item.invoiceDate="{ item }">
          {{ item.invoiceDate | formatDate("MM/dd/yyyy") }}
        </template>
        <template v-slot:item.paymentDate="{ item }">
          {{ item.paymentDate | formatDate("MM/dd/yyyy") }}
        </template>
        <template v-slot:item.paymentDueDate="{ item }">
          {{ item.paymentDueDate | formatDate("MM/dd/yyyy") }}
        </template>
        <template v-slot:item.amount="{ item }">
          {{ "$" + (item.amount / 100).toFixed(2) }}
        </template>
      </v-data-table>
    </PtSheet>

    <v-overlay v-model="dataTableLoading" class="align-center justify-center">
      <v-progress-circular
        color="primary"
        indeterminate
        size="64"
      ></v-progress-circular>
    </v-overlay>
  </v-container>
</template>

<script>
import ContentHeader from "@/views/ContentHeader.vue";
import PtSheet from "@/components/PtSheet.vue";
import { get } from "@/services/request";
import { invoicesAPI, annualSpendAPI } from "@/services/api";
import { formatDate, formatDollars } from "@/services/format";
import hasNotifications from "@/mixins/has-notifications";
import { isEmpty } from "@/services/util";

export default {
  name: "Invoices",
  components: { PtSheet, ContentHeader },
  mixins: [hasNotifications],
  data() {
    return {
      headers: [
        { text: "Client ID", value: "clientId" },
        { text: "Facility", value: "facility" },
        { text: "Account Name", value: "accountName" },
        { text: "Vendor Number", value: "vendorNumber" },
        { text: "COV ID", value: "covId" },
        { text: "Invoice Number", value: "invoiceNumber" },
        { text: "Invoice Date", value: "invoiceDate" },
        { text: "Payment Date", value: "paymentDate" },
        { text: "Payment Due Date", value: "paymentDueDate" },
        { text: "Payment Status", value: "paymentStatus" },
        { text: "Include in Annual Spend", value: "includeInAnnualSpend" },
        { text: "Payment Amount", value: "amount", align: "end" }
      ],
      items: [],
      itemsLength: 0,
      dataTableLoading: true,
      searchFilters: { covId: null },
      searchOptions: {
        page: 1,
        itemsPerPage: 50,
        sortBy: [],
        sortDesc: []
      },
      linkOptions: [
        { text: "All", value: null },
        { text: "Linked", value: true },
        { text: "Unlinked", value: false }
      ],
      includeInAnnualSpendOptions: [
        { text: "All", value: null },
        { text: "Included in Annual Spend", value: true },
        { text: "Excluded from Annual Spend", value: false }
      ],
      annualSpend: {}
    };
  },
  created() {
    this.searchFilters.includeInAnnualSpendOption = true;
    this.loadTableData();
  },
  filters: {
    formatDate,
    formatDollars
  },
  watch: {
    searchOptions: {
      handler() {
        this.loadTableData();
      },
      deep: true
    }
  },
  methods: {
    getFilters() {
      const filters = {};

      filters.vendorNumber = this.searchFilters.vendorNumber;
      filters.covId = this.searchFilters.covId;
      filters.covgId = this.searchFilters.covgId;
      filters.signupCode = this.searchFilters.signupCode;
      filters.linkOption = this.searchFilters.linkOption;
      filters.includeInAnnualSpendOption = this.searchFilters.includeInAnnualSpendOption;

      return filters;
    },
    loadTableData() {
      this.dataTableLoading = true;
      get(invoicesAPI, {
        page: this.searchOptions.page - 1, //vuetify uses 1-indexed page, server uses 0-indexed
        size: this.searchOptions.itemsPerPage,
        sortBy: this.searchOptions.sortBy.at(0),
        sortDesc: this.searchOptions.sortDesc.at(0),
        ...this.getFilters()
      }).then(response => {
        if (response && response.data) {
          this.itemsLength = response.data.total;
          this.items = response.data.content;
        }
        this.dataTableLoading = false;
      });
    },
    loadAnnualSpend() {
      get(
        annualSpendAPI.getUrl({ vendorNumber: this.searchFilters.vendorNumber })
      ).then(response => {
        if (response && response.data) {
          this.annualSpend = response.data;
        }
      });
    },
    reset() {
      this.searchFilters = {};
      this.searchFilters.includeInAnnualSpendOption = true;
      this.annualSpend = {};
      this.search();
    },
    search() {
      this.searchOptions.page = 1;
      this.loadTableData();
      if (this.searchFilters.vendorNumber) this.loadAnnualSpend();
      else this.annualSpend = {};
    },
    setCovId(value) {
      this.searchFilters.covId = value;
      this.search();
    },
    setVendorNumber(value) {
      this.searchFilters.vendorNumber = value;
      this.search();
    },
    isEmpty
  }
};
</script>

<style scoped lang="scss">
.filter-bar {
  .v-input {
    max-width: 200px;
  }
}
</style>

<style lang="scss">
#app #invoices .container {
  font-size: 1.5em !important;
}
</style>
